// Utilities for company business hours + close-mode flags.
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Lunes", tue: "Martes", wed: "Miércoles", thu: "Jueves", fri: "Viernes", sat: "Sábado", sun: "Domingo" };

function parseHHMM(s) {
  if (!s) return null;
  const [hh, mm] = String(s).split(":");
  const h = Number(hh);
  const m = Number(mm);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function dayKeyFromDate(d) {
  return DAY_KEYS[(d.getDay() + 6) % 7];
}

/** Resolve close_mode from either the new `close_mode` field or the legacy
 * `temporarily_closed` flag. */
export function getCloseMode(company) {
  if (!company) return "none";
  if (company.close_mode) return company.close_mode;
  if (company.temporarily_closed) return "today";
  return "none";
}

/** Returns { open: bool, reason: string, nextOpen?: {day, time, date}, mode }. */
export function companyStatus(company, at = new Date()) {
  if (!company) return { open: true, reason: null, mode: "none" };
  const mode = getCloseMode(company);
  if (mode === "permanent") {
    return { open: false, reason: "Cerrado permanentemente", mode };
  }

  const hours = company.business_hours;
  const hoursConfigured = hours && Object.keys(hours).length > 0;

  if (mode === "today") {
    const nextOpen = findNextOpenAfter(company, at, /* skipToday */ true);
    return {
      open: false,
      reason: "Cerrado hoy",
      mode,
      nextOpen,
    };
  }

  // mode === "none" — rely solely on the hours configuration
  if (!hoursConfigured) return { open: true, reason: null, mode };

  const key = dayKeyFromDate(at);
  const day = hours[key] || {};
  const minutes = at.getHours() * 60 + at.getMinutes();
  const o = parseHHMM(day.open);
  const c = parseHHMM(day.close);
  if (!day.closed && o !== null && c !== null) {
    const isOpen = o <= c ? (o <= minutes && minutes < c) : (minutes >= o || minutes < c);
    if (isOpen) return { open: true, reason: null, current: day, mode };
  }

  // Closed right now — find next open slot.
  const nextOpen = findNextOpenAfter(company, at, /* skipToday */ false);
  return {
    open: false,
    reason: day.closed ? `Cerrado los ${DAY_LABELS[key]}` : "Fuera de horario",
    mode,
    nextOpen,
  };
}

function findNextOpenAfter(company, at, skipToday) {
  const hours = company.business_hours || {};
  const minutes = at.getHours() * 60 + at.getMinutes();
  const startIdx = (at.getDay() + 6) % 7;
  const startStep = skipToday ? 1 : 0;
  for (let step = startStep; step < 8; step += 1) {
    const dayIdx = (startIdx + step) % 7;
    const dkey = DAY_KEYS[dayIdx];
    const d = hours[dkey];
    if (!d || d.closed) continue;
    const op = parseHHMM(d.open);
    if (op === null) continue;
    if (!skipToday && step === 0 && op <= minutes) continue; // already past today's open
    const date = new Date(at);
    date.setDate(date.getDate() + step);
    return { day: dkey, dayLabel: DAY_LABELS[dayIdx] ? DAY_LABELS[DAY_KEYS[dayIdx]] : DAY_LABELS[dkey], time: d.open, date };
  }
  return null;
}

/** Build a sorted list of 30-min slots available for a given date (Date obj)
 * based on the company's business hours. Returns [{label:"08:00", minutes: 480}]. */
export function slotsForDate(company, date, stepMin = 30) {
  const hours = company?.business_hours || {};
  const key = DAY_KEYS[(date.getDay() + 6) % 7];
  const d = hours[key];
  if (!d || d.closed) return [];
  const o = parseHHMM(d.open);
  const c = parseHHMM(d.close);
  if (o === null || c === null) return [];
  const out = [];
  let cur = o;
  const end = o <= c ? c : (24 * 60);
  while (cur < end) {
    const hh = String(Math.floor(cur / 60)).padStart(2, "0");
    const mm = String(cur % 60).padStart(2, "0");
    out.push({ label: `${hh}:${mm}`, minutes: cur });
    cur += stepMin;
  }
  return out;
}

/** True if the given Date falls on a day the company is open. */
export function isOpenDay(company, date) {
  const hours = company?.business_hours;
  if (!hours) return true;
  const key = DAY_KEYS[(date.getDay() + 6) % 7];
  const d = hours[key];
  if (!d) return true;
  if (d.closed) return false;
  return !!(d.open && d.close);
}

export { DAY_LABELS };
